import { AlertTriangle, Send, Upload } from "lucide-react";
import { useState, type FC } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";

import { useCaseDataOptional } from "../../data/CaseDataContext";
import { CaseUploadDialog } from "../../files/UploadDialog";
import { useSendIntake } from "../../files/sendIntake";
import { useWorkflows } from "../context/WorkflowsContext";

const intakeOnWarn =
  "bg-elevated shadow-xs inset-ring-line-3 hover:bg-elevated/85";

/**
 * The first step of Let CaseBlink Prepare while the case has no files: the
 * case starts with documents, so upload them or send the client an intake to
 * collect them. Every workflow below waits on this, and the step goes away
 * once a file is in Case Files.
 */
export const AddFilesFirst: FC = () => {
  const w = useWorkflows();
  const fs = useCaseDataOptional()?.fs;
  const intake = useSendIntake();
  const [uploading, setUploading] = useState(false);

  const loading = fs ? !fs.loaded || fs.isLoading || fs.reconnecting : false;
  if (w.facts.files > 0 || loading) return null;

  return (
    <section
      role="status"
      className="flex flex-col gap-3 rounded-lg border border-warn-line bg-warn-soft p-4"
      aria-labelledby="add-files-first"
      data-testid="add-files-first"
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle
          className="mt-0.5 size-4 shrink-0 text-warn"
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3
            id="add-files-first"
            className="text-base font-semibold text-warn"
          >
            Start By Adding Documents
          </h3>
          <p className="text-base text-fg">
            Upload what you have, or send the client an intake to collect it.
            Every step below works from what is in Case Files.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="md"
          variant="primary"
          onClick={() => setUploading(true)}
          disabled={!fs}
          data-testid="add-files-upload"
        >
          <Upload />
          Upload Files
        </Button>
        {intake.to ? (
          <Button
            size="md"
            variant="secondary"
            className={intakeOnWarn}
            asChild
          >
            <Link to={intake.to} data-testid="add-files-intake">
              <Send />
              {intake.label}
            </Link>
          </Button>
        ) : (
          <Button
            size="md"
            variant="secondary"
            className={intakeOnWarn}
            disabled
            title={intake.hint}
            data-testid="add-files-intake"
          >
            <Send />
            {intake.label}
          </Button>
        )}
      </div>
      {uploading && fs ? (
        <CaseUploadDialog
          open
          onOpenChange={(o) => !o && setUploading(false)}
          targetName="Case Files"
          parentId={null}
          onAdded={(nodes) => {
            fs.addNodes(nodes);
            void fs.refresh();
          }}
        />
      ) : null}
    </section>
  );
};
